import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { styled } from "@mui/material/styles";
import { Link } from "react-router-dom";
import { InvStatus } from "../../../libs/constants";

import styles from "./ListMonthlyInvoice.module.scss";
import { Stack } from "@mui/material";
import { getCurrentInvoices } from "../../../apis/dashboard";
import { InvoicesResponse } from "../../../apis/models/DashboardResponse";
import { formatNumber } from "../../../libs/helpers";

function convertCutOffDate(invoiceTo: string) {
  const date = new Date(invoiceTo);
  return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
}

const ListMonthlyInvoiceTable = () => {
  const month = new Date().getMonth();
  const [apiData, setApiData] = React.useState<InvoicesResponse[]>([]);
  const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#E8EEF6",
      color: "#1E53A3",
    },
  }));

  React.useEffect(() => {
    let isActive = true;
    getCurrentInvoices()
      .then((res) => {
        if (isActive) {
          // To avoid errors that occur in the dev environment
          const setData = res.data.data.filter((data: InvoicesResponse) => data.brand !== null)
          setApiData(setData);
        }
      })
      .catch((err) => console.log(err));

    return () => {
      isActive = false;
    }
  }, []);

  const displayData = React.useMemo(() => {
    apiData.sort((a, b) => {
      if (a.brand.brandName > b.brand.brandName) {
        return 1;
      } else {
        return -1;
      }
    })
    const unConfirmedData = apiData.filter(item => item.status === InvStatus.NOT_CONFIRMED);
    const confirmedData = apiData.filter(item => item.status !== InvStatus.NOT_CONFIRMED);
    const unConfirmedTotal = unConfirmedData.reduce((prev, item) => prev + item.totalPrice, 0);
    const confirmedTotal = confirmedData.reduce((prev, item) => prev + item.totalPrice, 0);
    const totalPrice = unConfirmedTotal + confirmedTotal;

    return {totalPrice, unConfirmedTotal, confirmedTotal, unConfirmedData, confirmedData}
  }, [apiData]);

  return (
    <div className={styles.componentContent}>
      <Stack direction="row" className={styles.componentHeader}>
        <h2 className={styles.componentTitle}>当月請求確認</h2>
        <span
          style={{
            marginBlockStart: "auto",
            marginBlockEnd: "1.5em",
            fontSize: "14px",
            marginLeft: "1.5em",
            color: "#1E53A3",
          }}
        >
          {month + 1}月売上総額：
        </span>
        <span
          style={{
            marginBlockStart: "auto",
            marginBlockEnd: "0.65em",
            fontSize: "26px",
          }}
        >
          {formatNumber(displayData.totalPrice)}
        </span>
      </Stack>
      <div className={styles.tablesCollection}>
        <div className={styles.leftTableContainer}>
          <div className={styles.tablesHeadText}>
            <span className={styles.isConfirmedText}>未確定の請求</span>
            <span
              style={{
                marginBlockStart: "auto",
                marginBlockEnd: "1.5em",
                fontSize: "12px",
                marginLeft: "1.5em",
                color: "#1E53A3",
              }}
            >
              合計金額 :
            </span>
            <span className={styles.grayText}>
              {formatNumber(displayData.unConfirmedTotal)}
            </span>
          </div>
          <div className={styles.leftTable}>
            <Table sx={{ height: "100%" }} aria-label="simple table" stickyHeader>
              <TableHead>
                <TableRow className={styles.headerTable}>
                  <StyledTableCell sx={{ borderTopLeftRadius: "10px", padding: "8px 10px" }}>
                    ブランド名
                  </StyledTableCell>
                  <StyledTableCell sx={{ width: "60px", padding: "8px 10px" }}>請求締日</StyledTableCell>
                  <StyledTableCell sx={{ width: "65px", padding: "8px 10px" }}>金額</StyledTableCell>
                  <StyledTableCell sx={{ minWidth: "70px", padding: "8px 10px" }}>担当者名</StyledTableCell>
                  <StyledTableCell
                    sx={{ borderTopRightRadius: "10px", width: "40px", padding: "8px 10px" }}
                  ></StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayData.unConfirmedData.map((data, index) => (
                  <TableRow
                    className={styles.tableRows}
                    key={index}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell sx={{padding: "8px 10px", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>{data.brand.brandName}</TableCell>
                    <TableCell sx={{padding: "8px 10px", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>{convertCutOffDate(data.to)}</TableCell>
                    <TableCell sx={{padding: "8px 10px", borderRight: "1px solid rgba(224, 224, 224, 1)" }} align="right">{formatNumber(data.totalPrice)}</TableCell>
                    <TableCell sx={{padding: "8px 10px", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>{data.brand.salesStaff?.userName ?? "-"}</TableCell>
                    <TableCell sx={{padding: "8px 10px" }}>
                      <Link to={`/invoice/${data.invoiceMasterId}`}>詳細&gt;</Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        <div className={styles.rightTableContainer}>
          <div className={styles.tablesHeadText}>
            <span className={styles.isNotConfirmedText}>確定済の請求</span>
            <span
              style={{
                marginBlockStart: "auto",
                marginBlockEnd: "1.5em",
                fontSize: "12px",
                marginLeft: "1.5em",
                color: "#1E53A3",
              }}
            >
              合計金額 :
            </span>
            <span className={styles.grayText}>
              {formatNumber(displayData.confirmedTotal)}
            </span>
          </div>
          <div className={styles.rightTable}>
            <Table sx={{ height: "100%" }} aria-label="simple table" stickyHeader>
              <TableHead>
                <TableRow className={styles.headerTable}>
                  <StyledTableCell sx={{ borderTopLeftRadius: "10px", padding: "8px 10px" }}>
                    ブランド名
                  </StyledTableCell>
                  <StyledTableCell sx={{ width: "60px", padding: "8px 10px" }}>請求締日</StyledTableCell>
                  <StyledTableCell sx={{ width: "65px", padding: "8px 10px" }}>金額</StyledTableCell>
                  <StyledTableCell sx={{ minWidth: "70px", padding: "8px 10px" }}>担当者名</StyledTableCell>
                  <StyledTableCell
                    sx={{ borderTopRightRadius: "10px", width: "40px", padding: "8px 10px" }}
                  ></StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayData.confirmedData.map((data, index) => (
                  <TableRow
                    className={styles.tableRows}
                    key={index}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell sx={{padding: "8px 10px", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>{data.brand.brandName}</TableCell>
                    <TableCell sx={{padding: "8px 10px", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>{convertCutOffDate(data.to)}</TableCell>
                    <TableCell sx={{padding: "8px 10px", borderRight: "1px solid rgba(224, 224, 224, 1)" }} align="right">{formatNumber(data.totalPrice)}</TableCell>
                    <TableCell sx={{padding: "8px 10px", borderRight: "1px solid rgba(224, 224, 224, 1)" }}>{data.brand.salesStaff?.userName ?? "-"}</TableCell>
                    <TableCell sx={{padding: "8px 10px" }}>
                      <Link to={`/invoice/${data.invoiceMasterId}`}>詳細&gt;</Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListMonthlyInvoiceTable;
